import { useEffect, useRef } from 'react';
import { useMap } from 'react-leaflet';

type Props = {
  coordinates: readonly (readonly [number, number])[];
  maxZoom?: number;
  padding?: number;
};

export const FitMapToCoordinates = ({ coordinates, maxZoom, padding = 50 }: Props) => {
  const map = useMap();
  const previousFitKey = useRef<string | null>(null);
  const wasHidden = useRef(false);
  const bounds = getCoordinateBounds(coordinates);
  const southLatitude = bounds?.[0][0];
  const westLongitude = bounds?.[0][1];
  const northLatitude = bounds?.[1][0];
  const eastLongitude = bounds?.[1][1];

  useEffect(() => {
    if (southLatitude == null || westLongitude == null || northLatitude == null || eastLongitude == null) {
      previousFitKey.current = null;
      wasHidden.current = false;
      return;
    }

    const container = map.getContainer();
    const fitKey = `${String(maxZoom)}:${padding}:${southLatitude}:${westLongitude}:${northLatitude}:${eastLongitude}`;
    const fitVisibleViewport = () => {
      const { clientHeight, clientWidth } = container;
      if (clientWidth === 0 || clientHeight === 0) {
        wasHidden.current = true;
        return;
      }

      // A visible resize must not undo the user's pan or zoom. Refit only for new content or after a hidden map opens.
      const shouldFit = wasHidden.current || previousFitKey.current !== fitKey;
      wasHidden.current = false;
      map.invalidateSize({ animate: false });
      if (!shouldFit) {
        return;
      }

      previousFitKey.current = fitKey;
      map.fitBounds(
        [
          [southLatitude, westLongitude],
          [northLatitude, eastLongitude]
        ],
        { maxZoom, padding: [padding, padding] }
      );
    };

    fitVisibleViewport();
    const resizeObserver = new ResizeObserver(fitVisibleViewport);
    resizeObserver.observe(container);

    return () => resizeObserver.disconnect();
  }, [eastLongitude, map, maxZoom, northLatitude, padding, southLatitude, westLongitude]);

  return null;
};

const getCoordinateBounds = (
  coordinates: readonly (readonly [number, number])[]
): [[number, number], [number, number]] | null => {
  const firstCoordinate = coordinates[0];
  if (!firstCoordinate) {
    return null;
  }

  const coordinateBounds = {
    minLatitude: firstCoordinate[0],
    minLongitude: firstCoordinate[1],
    maxLatitude: firstCoordinate[0],
    maxLongitude: firstCoordinate[1]
  };

  for (const [latitude, longitude] of coordinates) {
    coordinateBounds.minLatitude = Math.min(coordinateBounds.minLatitude, latitude);
    coordinateBounds.minLongitude = Math.min(coordinateBounds.minLongitude, longitude);
    coordinateBounds.maxLatitude = Math.max(coordinateBounds.maxLatitude, latitude);
    coordinateBounds.maxLongitude = Math.max(coordinateBounds.maxLongitude, longitude);
  }

  return [
    [coordinateBounds.minLatitude, coordinateBounds.minLongitude],
    [coordinateBounds.maxLatitude, coordinateBounds.maxLongitude]
  ];
};
